import React, { useEffect, useState, useRef, memo } from 'react';
import { RootState } from '../../store/store';
import { useSelector } from "react-redux";
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  IconButton,
  Typography,
  Tooltip,
  Dialog,
  DialogContent,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import html2canvas from 'html2canvas';
import ExpandDialog from '../expand-dialog/ExpandDialog';
import InfoIcon from '@mui/icons-material/Info';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { OpenInFull } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import FilterDropdown from '../filter-dropdown/FilterDropDown';
import { QlikEmbed } from '@qlik/embed-react';
import { getVisualizationProps, getValueColorsFromMasterDim } from '../../services/qlikService';
import { getObject } from '../../services/qlikObjectService';
import { useReady } from "../../hooks/useReady";
import { gtm } from '../../services/gtm';
import { useQlikObject } from '../../hooks/useQlikObject';
import { selectQlikAppSelections } from '../../store/slice/qlikSelectionSlice';
import { QLIK_APP_REF, THEME, CHART_DESCRIPTIONS } from '../../constants';
import './QlikVizPanel.scss';
import { exportData } from '../../services/qlikExportService';

interface QlikVizPanelProps {
  qlikVizName: string;
  qlikTitle?: string;
  enableSelections?: boolean;
  enableInteractions?: boolean;
  legendName?: string;
  expand?: boolean;
  flexColumn?: boolean;
  manualLegend?: Array<{ name: string; color: string }>;
  manualLegendTitle?: string;
  alwaysShow?: string;
  showCriteria?: boolean;
  noLegend?: boolean;
  svgToPng?: boolean;
  inDialog?: boolean;
  addElToExport?: string;
  addAsterisk?: boolean;
  fieldName?: string;
  altState?: string;
  qlikSort?: string;
  fieldLabel?: string;
  multiSelect?: boolean;
  filterDisable?: boolean;
  disableText?: string;
  sortExpression?: string;
  footnote?: string;
  equalHeight?: boolean;
  print?: boolean;
}

interface QlikVizTitleModel {
  title: string;
  subtitle: string;
  footnote: string;
}

interface LegendItem {
  name: string;
  value?: string;
  color: string;
  isPositive?: boolean;
}

// Create a memoized wrapper for QlikEmbed
const MemoizedQlikEmbed = memo(
  QlikEmbed,
  (prevProps:any, nextProps:any) => {
    // Return true if props are equal (don't re-render)
    // Return false if props changed (do re-render)
    return (
      prevProps.appId === nextProps.appId &&
      prevProps.objectId === nextProps.objectId &&
      prevProps.ui === nextProps.ui &&
      prevProps.iframe === nextProps.iframe &&
      prevProps.theme === nextProps.theme &&
      JSON.stringify(prevProps.context) === JSON.stringify(nextProps.context)
    );
  }
);

// Add display name for better debugging
MemoizedQlikEmbed.displayName = 'MemoizedQlikEmbed';

export const QlikVizPanel: React.FC<QlikVizPanelProps> = ({
  qlikVizName,
  qlikTitle,
  enableSelections = true,
  enableInteractions = false,
  legendName,
  expand = false,
  flexColumn = false,
  manualLegend,
  manualLegendTitle,
  alwaysShow,
  showCriteria = false,
  noLegend = false,
  svgToPng = false,
  inDialog = false,
  addElToExport,
  addAsterisk = false,
  fieldName,
  altState,
  qlikSort,
  fieldLabel,
  multiSelect = false,
  filterDisable = false,
  disableText,
  sortExpression,
  footnote,
  equalHeight = false,
  print = false,
}) => {
  const vizRef = useRef<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const [ready, setReady] = useState(false);
  // const [qlikEmbedReady, setQlikEmbedReady] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showExpandDialog, setShowExpandDialog] = useState(false);
  const [legendData, setLegendData] = useState<any[]>([]);
  const [legendColors, setLegendColors] = useState<any[]>(manualLegend || []);
  const [legendTitle, setLegendTitle] = useState({ title: '', subtitle: '' });
  const [selections, setSelections] = useState<any[]>([]);
  const [numSelections, setNumSelections] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyIcon, setCopyIcon] = useState<'copy' | 'check'>('copy');
  const [copyMessage, setCopyMessage] = useState('Copy Text');
  const [iconOpacity, setIconOpacity] = useState(0.3);
  const { qlikObject } = useReady("", "", qlikVizName);
  const qlikObjectLegend = useReady("", "", legendName).qlikObject;
  const { qlikObjectData, qlikObjectTitle, qlikObjectSubtitle, qlikObjectFootnote, qlikObjectColorMapRef } = useQlikObject(qlikVizName);
  const qlikObjectLegendData = useQlikObject(legendName || "").qlikObjectData;
  const qlikObjectLegendTitle = useQlikObject(legendName || "").qlikObjectTitle;
  // const qlikObjectLegendSubtitle = useQlikObject(legendName || "").qlikObjectSubtitle;
  const [vizProps, setVizProps] = useState<any>(null);
  const [vizLegendProps, setVizLegendProps] = useState<any>(null);
  const [interactions, setInteractions] = useState<any>(null);
  const qlikAppSelections = useSelector((state: RootState) => selectQlikAppSelections(state, QLIK_APP_REF));

  // Browser detection
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIE = /MSIE|Trident/.test(navigator.userAgent);

  const helpContent = CHART_DESCRIPTIONS[qlikVizName]?.text || null;
  // const helpContentParagraphs = helpContent?.split(/<p\s*\/?>/i);
  const helpContentParagraphs = helpContent
    ?.split(/<\/?p\s*>/i) // Split by both opening and closing p tags
    .map((p: string) => p.trim()) // Trim each paragraph
    .filter((p: string) => p !== ''); // Remove empty strings
  const helpNote = CHART_DESCRIPTIONS[qlikVizName]?.note || null;

  useEffect(() => {
    initQlikObject();
  }, [qlikVizName, legendName]);

  useEffect(() => {
    renderQlikObject();
  }, [qlikObject, qlikObjectLegend, inDialog]);

  useEffect(() => {
    if (legendName || showCriteria) {
      subscribeToSelections();
    }
  }, [qlikAppSelections, legendName, showCriteria, altState]);

  useEffect(() => {
    // Auto-play for specific chart (barchart race)
    if (qlikVizName === 'inc-growth-perc-ann-claim-ind') {
      createObserver();
    }
  }, [qlikVizName, vizRef]);

  const initQlikObject = async () => {
    try {
      // We still need visualization props even if the object already exists (e.g. second mount in dialog)
      if(!vizProps) {
        const vizPropsData = await getVisualizationProps(qlikVizName);
        setVizProps(vizPropsData);
        // Only request the object if it hasn't been created yet
        if(!qlikObject) {
          await getObject(qlikVizName, true, "", manualLegend ? true : false);
        }

        if(vizPropsData?.vizDef?.ui === 'analytics/chart') {
          setInteractions({
            interactions : {
              active: typeof enableInteractions === 'boolean' ? (enableInteractions ? true : false) : false,
              select: false,
              passive: true,
            },
          });
        } else {
          setInteractions({});
        }

        // Legend props: same reasoning—need them even if legend object already exists
        if(legendName && !vizLegendProps) {
          const vizLegendPropsData = await getVisualizationProps(legendName);
          setVizLegendProps(vizLegendPropsData);
          if(!qlikObjectLegend) {
            await getObject(legendName, true, "", true);
          }
        }
      }
    }
    catch (error) {
      console.error('Error initializing Qlik object:', error);
    }
  }

  const renderQlikObject = async () => {
    try {
      if (qlikObject) {
        // if (qlikObjectData && qlikObject?.qInfo?.qId === qlikObjectLegend?.qInfo?.qId) {
        if (legendName && qlikObjectData && qlikObject?.qInfo?.qId === qlikObjectLegend?.qInfo?.qId) {
          setLegendData(qlikObjectData);
        }

        if (qlikObjectLegendData && (qlikObjectLegend?.qInfo?.qId !== qlikObject?.qInfo?.qId) ) {
          setLegendData(qlikObjectLegendData);
        }

        if(legendName && qlikObjectLegend) {
          if(qlikObjectColorMapRef.length > 0) {
            const colors = await getValueColorsFromMasterDim(QLIK_APP_REF, qlikObjectColorMapRef);
            setLegendColors(colors);
          }
        }

        setReady(true);
      }
    } catch (error) {
      console.error('Error initializing Qlik object:', error);
    }
  };

  const createObserver = () => {
    observerRef.current = new MutationObserver((_, observer) => {
      const isPlayButtonLoaded = vizRef.current?.querySelector(`#playButton`)
      if (isPlayButtonLoaded) {
        setTimeout(() => {
          const playButton = document.querySelector(`#playButton`) as HTMLElement;  //testing play in iframe with Nov2024
          playButton?.click();
        }, 1000);
        observer.disconnect();
      }
    });

    if(vizRef.current) {
      observerRef.current?.observe(vizRef.current, {
        childList: true,
        subtree: true,
      });
    }
  }

  const subscribeToSelections = async () => {
    try {
      setSelections(qlikAppSelections);
      setNumSelections(qlikAppSelections?.length);
    } catch (error) {
      console.error('Error subscribing to selections:', error);
    }
  };

  const handleToggleHelp = () => {
    setShowHelp((prev) => !prev);
    if (!showHelp) {
      gtm.chartInteraction('Show Chart Description', qlikObjectTitle);
    } else {
      gtm.chartInteraction('Back to Chart from Desc.', qlikObjectTitle);
      setCopyMessage('Copy Text');
      setCopyIcon('copy');
    }
  };

  const handleExpand = () => {
    setShowExpandDialog(true);
    gtm.chartInteraction('Expand Object', qlikObjectTitle);
  };

  const handleCloseExpandDialog = () => {
    setShowExpandDialog(false);
  };

  const handleCopyText = async () => {
    if (!helpContent) return;

    try {
      // Strip HTML tags for plain text copy
      const plainText = helpContent.replace(/<[^>]*>/g, '');
      await navigator.clipboard.writeText(plainText);
      setCopyMessage('Text Copied!');
      setCopyIcon('check');
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
      gtm.chartInteraction('Copy Chart Description', qlikObjectTitle);
    } catch (error) {
      setCopyMessage("Sorry, there was an issue with this feature.");
    }
  };

  const handleDownloadImage = async () => {
    if (!vizRef.current) return;

    setShowExportDialog(true);
    gtm.chartInteraction('Download Chart Image', qlikObjectTitle);

    try {
      const result = await exportData(qlikVizName, 'image');

      // Create canvas
      const canvas = canvasRef.current;
      if (!canvas) return;

      const context = canvas.getContext('2d');
      if (!context) return;

      const canvasWidth = qlikObjectLegend?.qInfo?.qId || manualLegend ? 1100 : 850;
      canvas.width = canvasWidth;
      canvas.height = 700;

      // White background
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);

      // Load and draw chart image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Draw chart
        context.drawImage(img, 0, 120);

        // Draw logo
        const logoImg = new Image();
        logoImg.src = '/assets/images/logos/chubb-grey.png';
        logoImg.onload = async () => {
          context.drawImage(logoImg, canvas.width - 230, 650);

          // Draw title
          context.fillStyle = 'black';
          context.font = "24px 'Chubb Publico Text Web'";
          context.fillText(qlikObjectTitle, 15, 50);

          // Draw subtitle
          context.fillStyle = '#777';
          context.font = "18px 'Chubb Publico Text Web'";
          context.fillText(qlikObjectSubtitle, 15, 80);

          // Draw footnote
          context.fillText(qlikObjectFootnote, 15, 680);

          // Check if legend exists and render it with html2canvas
          const legendElement = document.getElementById(`${vizProps?.vizDef?.id}_legend`);
          if (legendElement) {
            try {
              const legendCanvas = await html2canvas(legendElement, {
                backgroundColor: 'transparent',
                width: 250,
                height: 500,
              });
              context.drawImage(legendCanvas, canvas.width - legendCanvas.width - 20, 120);

              // Check if additional element needs to be exported
              if (addElToExport) {
                const additionalElement = document.getElementById(addElToExport);
                if (additionalElement) {
                  const secFooterCanvas = await html2canvas(additionalElement, {
                    backgroundColor: 'transparent',
                    width: canvas.width,
                    height: 100,
                  });
                  context.drawImage(secFooterCanvas, 15, canvas.height - 100);
                }
              }
            } catch (error) {
              console.error('Error rendering legend with html2canvas:', error);
            }
          } else if (addElToExport) {
            // No legend but has additional element to export
            const additionalElement = document.getElementById(addElToExport);
            if (additionalElement && html2canvas) {
              try {
                const secFooterCanvas = await html2canvas(additionalElement, {
                  backgroundColor: 'transparent',
                  width: canvas.width,
                  height: 100,
                });
                context.drawImage(secFooterCanvas, 0, canvas.height - 100);
              } catch (error) {
                console.error('Error rendering additional element with html2canvas:', error);
              }
            }
          }

          // Save canvas
          saveCanvas(canvas);
        };
      };

      // Fetch image data
      // const response = await fetch(result, { credentials: 'include' });
      const blob = await result.blob();
      img.src = URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error downloading image:', error);
      setShowExportDialog(false);
    }
  };

  const handleDownloadSvg = async () => {
    const svg = document.querySelector(`#${qlikObject?.qInfo?.qId} svg`) as any;
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const bbox = svg.getBBox();
    canvas.width = bbox.width + 100;
    canvas.height = bbox.height + 220;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // White background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Clone and serialize SVG
    const copy = svg.cloneNode(true) as SVGElement;
    copyStylesInline(copy, svg);
    const data = new XMLSerializer().serializeToString(copy);
    const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const img = new Image();
    img.onload = async () => {
      ctx.drawImage(img, 0, 120);

      // Draw logo
      const logoImg = new Image();
      logoImg.src = '/assets/images/logos/chubb-grey.png';
      logoImg.onload = async () => {
        ctx.drawImage(logoImg, canvas.width - 230, canvas.height - 50);

        // Draw title
        ctx.fillStyle = 'black';
        ctx.font = "24px 'Chubb Publico Text Web'";
        ctx.fillText(qlikObjectTitle, 15, 50);

        // Draw subtitle
        ctx.fillStyle = '#777';
        ctx.font = "18px 'Chubb Publico Text Web'";
        ctx.fillText(qlikObjectSubtitle, 15, 80);
        ctx.fillText(qlikObjectFootnote, 15, 700);

        saveCanvas(canvas);
        URL.revokeObjectURL(url);
      };
    };
    img.src = url;

    gtm.chartInteraction('Download Chart Image', qlikObjectTitle);
  };

  const copyStylesInline = (destinationNode: any, sourceNode: any) => {
    const containerElements = ['svg', 'g'];
    for (let cd = 0; cd < destinationNode.childNodes.length; cd++) {
      const child = destinationNode.childNodes[cd];
      if (containerElements.includes(child.tagName)) {
        copyStylesInline(child, sourceNode.childNodes[cd]);
        continue;
      }
      const style = window.getComputedStyle(sourceNode.childNodes[cd]);
      if (!style) continue;
      for (let st = 0; st < style.length; st++) {
        child.style.setProperty(style[st], style.getPropertyValue(style[st]));
      }
    }
  };

  const saveCanvas = (canvas: HTMLCanvasElement) => {
    if ((navigator as any).msSaveOrOpenBlob) {
      const blob = (canvas as any).msToBlob();
      (navigator as any).msSaveOrOpenBlob(blob, `${qlikObjectTitle}.png`);
    } else {
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${qlikObjectTitle}.png`;
      link.click();
    }
    setShowExportDialog(false);
  };

  const foundInSelections = (name: string): boolean => {
    if (!selections) return false;
    return selections.some((el) => el.qSelected.includes(name));
  };

  const renderLegend = () => {
    if (!legendData?.length && !manualLegend?.length) return null;

    const legendItems: LegendItem[] = manualLegend
      ? manualLegend
          .filter((item) => item.name === alwaysShow || foundInSelections(item.name))
          .map((item) => ({
            name: item.name,
            color: item.color,
          }))
      : legendData.map((row) => {
          const colorItem = legendColors.find((c: any) => c.value === row[0].qText);
          return {
            name: row[0].qText,
            value: row[1]?.qText?.replace('-', ''),
            color: colorItem?.baseColor?.color || '#000',
            isPositive: row[1]?.qNum >= 0,
          };
        });

    return (
      <Grid
        id={`${vizProps?.vizDef?.id}_legend`}
        className="custom-legend"
        size={{ xs: flexColumn ? 12 : print ? 4 : 12, md: flexColumn ? 12 : print ? 4: 3.6 }}
        // size={3}
        sx={{ maxWidth: '250px', minWidth: print ? '' : '250px', margin: '1rem auto 0'}}
        // sx={{ maxWidth: '250px', margin: '1rem auto 0'}}
      >
        <Box
          sx={{
            borderLeft: { xs: print ? 1: 0, md: 1 },
            borderLeftColor: { xs: print ? 'divider' : 'transparent', md: 'divider' },
            borderTop: { xs: print ? 0 : 1, md: 0 },
            borderTopColor: { xs: print ? 'transparent' : 'divider', md: 'transparent' },
            borderColor: 'divider',
            pl: { xs: print ? 2 : 0, md: 2 },
            pt: { xs: print ? 0 : 2, md: 0 },
            opacity: showHelp ? 0 : 1,
            transition: 'opacity 0.3s',
          }}
        >
          {!manualLegend && qlikObjectLegend?.qInfo?.qId !== qlikObject?.qInfo?.qId && (
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'var(--mui-custom-grey)' }}>
              {numSelections ? qlikObjectLegendTitle : 'Make selections to compare to overall'}
            </Typography>
          )}

          {manualLegendTitle && (
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'var(--mui-custom-grey)' }}>
              {manualLegendTitle}
            </Typography>
          )}

          <Stack spacing={0.5}>
            {legendItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pb: 0.5,
                  fontSize: '0.875rem',
                }}
              >
                <Box
                  component="svg"
                  className="swatch"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  sx={{
                    width: 20,
                    height: 20,
                    mr: 2,
                    flexShrink: 0,
                  }}
                >
                  <rect width="20" height="20" fill={item.color} />
                </Box>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {item.name}
                </Typography>
                {item.value && qlikObjectLegend?.qInfo?.qId !== qlikObject?.qInfo?.qId && numSelections > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: item.isPositive ? 'success.main' : 'error.main',
                      fontWeight: 600,
                    }}
                  >
                    {item.value}{' '}
                    <Box component="span" sx={{ fontSize: '1rem' }}>
                      {item.isPositive ? '▲' : '▼'}
                    </Box>
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      </Grid>
    );
  };



  return (
    <>
      <Card
        variant={print ? 'outlined' : 'elevation'}
        sx={{
          mb: print ? 1: 3,
          border: print ? 0: null,
          borderTop: 3,
          borderColor: 'primary.main',
          borderRadius: 0,
          position: 'relative',
          overflow: 'visible',
          ...(equalHeight && {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
          }),
          '&:hover .chart-icons, & .chart-icons:hover': {
            transform: 'translateY(-100%)',
            clipPath: 'inset(0 0 0 0)',
            opacity: 1,
          },
        }}
        onMouseEnter={() => setIconOpacity(1)}
        onMouseLeave={() => setIconOpacity(0.3)}
      >
        {/* Hidden canvas for export */}
        <canvas ref={canvasRef} width={1200} height={800} style={{ display: 'none' }} />

        {/* Chart action icons */}
        <Box
          className="chart-icons"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: 'primary.main',
            display: 'flex',
            transform: 'translateY(-50%)',
            clipPath: 'inset(0 0 50% 0)',
            opacity: iconOpacity,
            transition: 'transform 200ms ease, clip-path 200ms ease, opacity 200ms ease',
            willChange: 'transform, clip-path, opacity',
            // transition: 'all 0.3s',
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          {helpContent && (
            <Tooltip title="Chart Description" placement="top">
              <IconButton
                size="small"
                onClick={handleToggleHelp}
                sx={{ color: 'white', borderRadius: 0, width: 32, height: 32 }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {!svgToPng && !isSafari && (
            <Tooltip title="Download Chart" placement="top">
              <IconButton
                size="small"
                onClick={handleDownloadImage}
                sx={{ color: 'white', borderRadius: 0, width: 32, height: 32, display: { xs: 'none', sm: 'flex' } }}
              >
                <CameraAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {svgToPng && !isSafari && !isIE && (
            <Tooltip title="Download Chart" placement="top">
              <IconButton
                size="small"
                onClick={handleDownloadSvg}
                sx={{ color: 'white', borderRadius: 0, width: 32, height: 32, display: { xs: 'none', sm: 'flex' } }}
              >
                <CameraAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {expand && (
            <Tooltip title="Expand Chart" placement="top">
              <IconButton
                size="small"
                onClick={handleExpand}
                sx={{ color: 'white', borderRadius: 0, width: 32, height: 32, display: { xs: 'none', sm: 'flex' } }}
              >
                <OpenInFull fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Card Header */}
        <CardHeader
          sx={{
            backgroundColor: '#f4f4f4',
            borderRadius: 0,
            '& .MuiCardHeader-title': {
              fontSize: '1rem',
              lineHeight: '1.25rem',
              color: 'text.primary',
            },
            '& .MuiCardHeader-subheader': {
              fontSize: '0.875rem',
              fontStyle: 'italic',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          title={
            <>
              {qlikObjectTitle} {addAsterisk && '*'}
            </>
          }
          subheader={qlikObjectSubtitle}
        />

        {/* Card Content */}
        <CardContent
          sx={{
            position: 'relative',
            ...(equalHeight && {
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }),
          }}
        >
          <Grid container spacing={2}>
            {/* Filter */}
            {fieldName && (
              <Grid size={{ xs: 12 }}>
                <FilterDropdown
                  formName="Page Filters"
                  fieldName={fieldName}
                  fieldLabel={fieldLabel || fieldName}
                  qlikSort={qlikSort}
                  sortExpression={sortExpression}
                  altState={altState}
                  multiSelect={multiSelect}
                />
              </Grid>
            )}

            {/* Visualization Container */}
            <Grid 
              size={{
                xs: flexColumn ? 12 : print ? 8 : 12, 
                md: flexColumn ? 12 : print ? 8 : (legendData?.length || manualLegend?.length) && !flexColumn ? 8.4 : 12 
              }}
            >
              <Box
                id={inDialog ? `modal_${qlikObject?.qInfo?.qId}` : qlikObject?.qInfo?.qId}
                ref={vizRef}
                sx={{
                  height: 350,
                  // opacity: showHelp ? 0 : 1,
                  transition: 'opacity 0.3s',
                  position: 'relative',
                  zIndex: 0
                }}
                className="object-placeholder"
              >
                {!ready && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                )}
                {/* {vizProps && ready && qlikEmbedReady &&  ( */}
                {vizProps && ready &&  (
                  <MemoizedQlikEmbed
                    appId={ vizProps?.appId }
                    ui={ vizProps?.vizDef?.ui }
                    objectId={ vizProps?.vizDef?.id }
                    iframe={ vizProps?.vizDef?.iframe }
                    theme={ THEME }
                    context={ interactions }
                    key={inDialog ? 'modal' : 'normal'}
                  />
                )}
              </Box>
            </Grid>

            {/* Legend */}
            {(legendData?.length > 0 || manualLegend) && renderLegend()}

            {/* Help Content */}
            {helpContent && (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'white',
                    opacity: showHelp ? 1 : 0,
                    maxHeight: showHelp ? '100%' : 0,
                    overflow: 'hidden',
                    transition: 'all 0.3s',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 2,
                  }}
                >
                  <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ 
                        color: 'text.secondary', 
                        fontSize: '0.875rem', 
                        lineHeight: 1.7,
                        '& > span': {
                          display: 'block',
                          marginBottom: '1em',
                          '&:last-child': {
                            marginBottom: 0,
                          },
                        },
                      }}
                    >
                      {helpContentParagraphs?.map((paragraph: string, idx: number) => (
                        <span key={idx}>
                          {paragraph}
                        </span>
                      ))}
                    </Typography>
                    {helpNote && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#eee', fontStyle: 'italic', color: 'text.secondary' }}>
                        <strong>Please Note:</strong>
                        <br />
                        {helpNote}
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderTop: 1,
                      borderColor: 'divider',
                      pt: 2,
                    }}
                  >
                    <IconButton
                      onClick={handleCopyText}
                      sx={{
                        // backgroundColor: '#fcfcfc',
                        borderRadius: 0,
                        ...(copySuccess && {
                          '& .MuiSvgIcon-root': {
                            animation: 'success-animation 0.75s 1 forwards',
                          },
                        }),
                      }}
                    >
                      {copyIcon === 'copy' ? <ContentCopyIcon /> : <CheckIcon />}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {copyMessage}
                      </Typography>
                    </IconButton>

                    <IconButton
                      onClick={handleToggleHelp}
                      sx={{
                        borderRadius: 0,
                      }}
                    >
                      <UndoIcon />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Back to the Chart
                      </Typography>
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>

        {/* Card Footer */}
        <CardActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 2,
            borderTop: 1,
            borderTopColor: 'divider',
          }}
          className='panel-footer'
        >
          <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary', fontStyle: 'italic' }}>
            {qlikObjectFootnote}
          </Typography>
          <Box component="img" src="/assets/images/logos/chubb-grey.png" sx={{ width: 100 }} alt="Chubb" />
        </CardActions>
      </Card>

      {/* Expand Dialog */}
      <ExpandDialog
        open={showExpandDialog}
        onClose={handleCloseExpandDialog}
        qlikVizName={qlikVizName}
        legendName={legendName}
        enableInteractions={enableInteractions}
      />

      {/* Export Dialog */}
      <Dialog open={showExportDialog} maxWidth="sm">
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>
            Your chart is being exported, please wait
          </Typography>
          <CircularProgress sx={{ mt: 2 }} />
        </DialogContent>
      </Dialog>

      {/* CSS Keyframes for copy success animation */}
      <style>{`
        @keyframes success-animation {
          0%, 100% { transform: scale(1); color: rgb(51, 51, 51); }
          50% { transform: scale(1.5); color: #0c800c; }
        }
      `}</style>
    </>
  );
};

export default QlikVizPanel;